/**
 * API service for user invitations
 */
import axios from 'axios';
import { apiGet, apiPost, apiDelete } from '../../../shared/api/axiosInstance';
import { API_BASE_URL } from '../../../shared/api/config';
import { publicTokenHeaders } from '../../../shared/api/publicToken';

/**
 * The invitation VERIFY call is public and authenticates with the invitation
 * token itself, so it must bypass the shared axios instance — that one injects
 * the logged-in user's JWT, which is the wrong (and, for a signed-out invitee,
 * absent) credential. The token travels in `X-Public-Token`, never in the URL:
 * a URL reaches browser history, `Referer` and the ALB/CloudFront access logs.
 */
const publicAxios = axios.create({ baseURL: API_BASE_URL });

const INVITATIONS_URL = '/api/v1/invitations';

// ========== Types ==========

export interface Invitation {
  id: string;
  tenant_id: string;
  email: string;
  is_admin: boolean;
  invited_by_user_id: string | null;
  invited_by_name?: string;
  company_id?: string;
  expires_at: string;
  created_at: string;
}

export interface CreateInvitationRequest {
  email: string;
  is_admin?: boolean;
}

export interface CreateInvitationResponse {
  message: string;
  invitation_id: string;
  email: string;
  expires_at: string;
}

export interface VerifyInvitationResponse {
  valid: boolean;
  email: string;
  tenant_id: string;
  tenant_name: string;
  role: string;
  invited_by_name: string;
  expires_at: string;
}

// ========== API ==========

export const invitationApi = {
  // List pending invitations for current tenant
  listInvitations: async (): Promise<Invitation[]> => {
    return apiGet<Invitation[]>(INVITATIONS_URL);
  },

  // Create and send invitation email
  createInvitation: async (data: CreateInvitationRequest): Promise<CreateInvitationResponse> => {
    return apiPost<CreateInvitationResponse>(INVITATIONS_URL, data);
  },

  // Revoke an invitation
  revokeInvitation: async (id: string): Promise<void> => {
    await apiDelete(`${INVITATIONS_URL}/${id}`);
  },

  // Verify an invitation token (public endpoint, credential in a header)
  verifyInvitation: async (token: string): Promise<VerifyInvitationResponse> => {
    const response = await publicAxios.get<VerifyInvitationResponse>(
      `${INVITATIONS_URL}/verify`,
      { headers: publicTokenHeaders(token) }
    );
    return response.data;
  },
};
